import Enumerable from "linq";
import { DomainRegistry } from "../../../../domain";
import {
  Document,
  ImageSourceKinds,
  OverwriteUpdater,
  DocumentImageUpdateInformation,
  Section,
  ImageKinds,
  EditPhaseKinds,
} from "../../../../domain/document";
import {
  AbstractCheckableItem,
  AbstractImageDrawableItem,
  IItem,
  instanceOfIEmptyValidatable,
  instanceOfIImageDrawable,
  instanceOfIQuotationRejecatable,
  instanceOfIRuleInjectable,
  MovableTextBoxItem,
  SignItem,
  TextBoxItem,
} from "../../../../domain/document/items";
import { instanceOfIQuotable } from "../../../../domain/document/items/IQuotable";
import {
  MoneyFormatInitializer,
  ItemHeightInitializer,
  HisInformationApplicator,
  DepartmentRelatedHisInformationApplicator,
} from "../helpers";
import { EditingDocument, PasteExecutor } from "..";
import { TimeMeasure } from "../../../../utilities";
import { IConfiguration } from "../../../../domain/configuration";
import store from "../../../../store";
import { LoggerFactory } from "../../../../utilities/logger";

class QuoteInformation {
  privateId: number;
  name: string;
  subName: string;
  isEmpty: boolean;
}

export class EditingDocumentMerger {
  private readonly _logger = LoggerFactory.getLogger();
  
  constructor(public source: EditingDocument) {
  }

  get claioImageName(): string {
    return (store.getters.configuration as IConfiguration).behavior.collaboration.claio.imageName;
  }

  get isOtherPatientDocumentQuotable(): boolean {
    return (store.getters.configuration as IConfiguration).behavior.document.editor.quotation.isOtherPatientDocumentQuotable;
  }

  get shouldApplyValueToEmptyObjectOnly(): boolean {
    return (store.getters.configuration as IConfiguration).behavior.document.editor.quotation.shouldApplyValueToEmptyObjectOnly;
  }

  isSameFormat(source: Document): boolean {
    // もともとはMasterCodeの比較で判定していたが、OriginalFormatIdの比較で問題なしとの指摘有り、OriginalFormatIdのみで判定するように修正している
    if (source.attributes.format.originalId === null || source.attributes.format.originalId === undefined) { return false; }
    return (source.attributes.format.originalId === this.source.source.attributes.format.originalId);
  }

  /**
   * 引用・転記の処理
   * ★引用 (isByNameがfalse)
   * ・同じマスターコードの文書
   * ・ObjectIdをキーにする
   * ・ObjectIdがない項目は名称が一致する、かつ引用・転記が許可された項目に値が入る
   * ・画像も持ってくる
   * ・続紙はいったん削除する
   * ・元の文書の続紙を丸ごと持ってくる（画像も）
   * ・文書・続紙内の追加オブジェクトも持ってくる
   * 
   * ★転記 (isByNameがtrue)
   * ・異なるマスターコードの文書
   * ・名称をキーにする
   * ・画像は持ってこない方針だったが、持ってくるように修正した（DMOで要望あり＋V1と挙動を合わせる）
   * ・現状の続紙は削除しない
   * ・続紙は持ってこない（文字サイズ自動縮小機能でオブジェクトに紐付いているテキストは持ってくる ※未実装）
   * ・追加オブジェクトは持ってこない
   */
  mergeDocument(source: Document, isByName: boolean, forceUpdate: boolean = false, shouldQuoteTemplateId: boolean = false): void {
    TimeMeasure.log("引用・転記の値のマージ処理", () => {
      // 引用・転記元の文書IDを更新
      this.source.attributes.quotedDocumentId = source.attributes.quotedDocumentId;

      const shouldCopyExtraSections = this.shouldCopyExtraSections(isByName, forceUpdate);
      if (shouldCopyExtraSections) {
        this.removeExtraSections();
      }
      const isSamePatient = this.isSamePatientDocument(source);
      const ignoreSourceItemPrivateIds = this.getIgnoreValueSourceItemPrivateIds(source, isByName);
      
      // 続紙は引用・転記させない(先頭のitemSetsに格納されているitemsだけをコピー元対象とする)
      const handledPrivateIds: number[] = [];
      const updatedPrivateIds: number[] = [];
      Enumerable.from(source.itemSets[0].items)
        .where(i => !!i && this.shouldQuoteFrom(i))
        .forEach(i => this.mergeObjectValue(source.documentId, source.itemSets[0].formatId, i, isByName, isSamePatient, handledPrivateIds, forceUpdate, shouldQuoteTemplateId, ignoreSourceItemPrivateIds, updatedPrivateIds));
  
      // copyAdditionalItems内でコピーする・しないを判定している（将来的にオブジェクトの種類でコピーする・しないを制御する可能性あり）
      this.copyAdditionalItems(source, isByName, forceUpdate, isSamePatient);
      
      if (shouldCopyExtraSections) {
        this.copyExtraSections(source, isSamePatient, isByName);
      }

      // 金額表示フォーマットの初期化
      const moneyFormatInitializer = new MoneyFormatInitializer(this.source);
      moneyFormatInitializer.initialize(true);

      // オブジェクト高さの初期化
      const itemHeightInitializer = new ItemHeightInitializer(this.source);
      itemHeightInitializer.initialize(true);

      // 診療科と紐付いたHIS項目の更新
      this.updateDepartmentRelatedHisItems(source);

      // v1動作では文書間転記の場合文書内転記を最後に処理してる模様.文書内転記後に文書間転記で上書きされるとv1と値が変わってしまう.
      // 対策として、文書間転記についてはマージが終わった後に改めて反映させる.
      // その際は、更新されたオブジェクトだけでなく、全ての文書内転記対象を反映させる.
      // (通常の文書間転記の初期化ではv1同様コンボボックスのみが反映されるようにしているが、文書間転記時はv1も本動作.)
      this.source.executeTextPaste(true);
      this.source.executeDateLink();
      
      // 複数のオブジェクトから文書内転記されているオブジェクトへの救済ロジック
      this.overwriteMultiPastedObjects(source, isByName, ignoreSourceItemPrivateIds);

      // TODO: 時刻からの日付の連動と手入力が競合しているオブジェクトの救済ロジックあったほうがよい？

      if (this.source.currentSectionIndex >= this.source.sections.length) {
        // 転記の場合は続紙を削除するので表示しているページが無くなっている場合がありうる
        this.source.currentSectionIndex = this.source.sections.length - 1;
      }
    },
    "(isByName:" + isByName + ")");
  }

  private shouldCopyExtraSections(isByName: boolean, forceUpdate: boolean): boolean {
    if (forceUpdate) { return true; }
    const configuration = store.getters.configuration as IConfiguration;
    return isByName
      ? configuration.behavior.document.editor.quotation.shouldCopyExtraSections.byObjectName
      : configuration.behavior.document.editor.quotation.shouldCopyExtraSections.byObjectId;
  }

  /**
   * 診療科と紐付いたHIS項目の更新（複数施設対応している案件で施設をまたがって引用・転記したときの対応）
   */
  private updateDepartmentRelatedHisItems(source: Document): void {
    const applicator = new DepartmentRelatedHisInformationApplicator(this.source);
    if (applicator.shouldUpdateDepartmentRelatedHisInformation(source.attributes.department)) {
      this._logger.info("診療科に紐付いたHIS情報を更新します。");
      applicator.apply(this.source.attributes.department);
    }
  }

  /**
   * V1の転記の挙動にあわせるために、重複する名称がある空のフリー入力・移動可能テキストをスキップ対象として抽出している
   * 重複する名称が無い場合は空でも反映されるのと、一部のオブジェクトだけスキップするので中途半端な挙動になっている
   * 将来的には仕様を見直すことになる見込み
   */
  private getIgnoreValueSourceItemPrivateIds(source: Document, isByName: boolean): number[] {
    if (!isByName) { return[]; }

    const infos: QuoteInformation[] = [];
    for (const item of source.itemSets[0].items) {
      if (!item || !this.shouldQuoteFrom(item)) { continue; }
      if (item instanceof TextBoxItem) {
        if (!item.name) { continue; }
        const info = new QuoteInformation();
        info.privateId = item.privateId;
        info.name = item.name;
        info.subName = (item.subName != null) ? item.subName : "";
        info.isEmpty = item.isEmpty;
        infos.push(info);
      }
      if (item instanceof MovableTextBoxItem) {
        if (!item.name) { continue; }
        const info = new QuoteInformation();
        info.privateId = item.privateId;
        info.name = item.name;
        info.subName = "";
        info.isEmpty = item.isEmpty;
        infos.push(info);
      }
    }
    const ignoreSourceItemPrivateIds: number[] = [];
    for (const info of infos) {
      if (!info.isEmpty) {
        // 値がある場合はスキップしない
        continue;
      }
      // 自分以外で転記のキーが重複するオブジェクトがあるかどうか
      const isNameDuplicated = Enumerable.from(infos)
        .any(i => (i.privateId !== info.privateId) && (i.name === info.name) && (i.subName === info.subName));

      if (isNameDuplicated) {
        ignoreSourceItemPrivateIds.push(info.privateId);
      }
    }
    return ignoreSourceItemPrivateIds;
  }

  private overwriteMultiPastedObjects(source: Document, isByName: boolean, ignoreSourceItemPrivateIds: number[]): void {
    const multiPastedObjectPrivateIds = this.getMultiPastedObjectPrivateIds();
    for (const multiPastedObjectPrivateId of multiPastedObjectPrivateIds) {

      const destinationItem = this.source.itemSets[0].items[multiPastedObjectPrivateId];
      // 文書内転記先のオブジェクトは固定文字で引用・転記処理を除外する判定はしない
      if (!instanceOfIQuotable(destinationItem) || !destinationItem.shouldQuote || !destinationItem.supportedQuotable) { continue; }
      const sourceItem = this.findValueSourceItem(source, destinationItem, isByName, ignoreSourceItemPrivateIds);
      if (!sourceItem) { continue; }

      this._logger.debug("重複して文書内転記されている可能性のあるオブジェクトの値を復旧します。objectId: " + destinationItem.id + ", privateId: " + destinationItem.privateId);
      const updater = new OverwriteUpdater(sourceItem, isByName, false);
      const cloned = destinationItem.clone();
      const isValueUpdated = updater.updateValues(cloned);
      if (isValueUpdated) {
        this.source.updateItem(cloned, {
          itemSetId: 0,
          isQuoting: true,
        });
      }
    }
  }

  findValueSourceItem(sourceDocument: Document, item: IItem, isByName: boolean, ignoreSourceItemPrivateIds: number[]): IItem {
    if (!item || item.isAdditional) { return null; }

    const items: IItem[] = [];
    for (const source of sourceDocument.itemSets[0].items) {
      if (!source) { continue; }
      const updater = new OverwriteUpdater(source, isByName, false);
      if (!updater.isKeyExist()) { continue; }
      if (ignoreSourceItemPrivateIds.indexOf(source.privateId) >= 0) { continue; }
      if (updater.isKeyMatched(item)) {
        items.push(source);
      }
    }
    // 転記時に名称が重複していて対象がひとつに定まらない場合は、救済しない（文書内転記しているので基本的には一つに定まるはず）
    return (items.length === 1)
      ? items[0]
      : null;
  }

  private getMultiPastedObjectPrivateIds(): number[] {
    const pasteExecutor = new PasteExecutor(this.source, true);
    return pasteExecutor.getMultiPastedObjectPrivateIds(0);
  }

  private isSamePatientDocument(valueSource: Document): boolean {
    return (this.source.source.attributes.patient.id === valueSource.attributes.patient.id);
  }

  private shouldQuoteFrom(item: IItem): boolean {
    return !item.isAdditional && !item.isDeleted && !this.isQuoteRejectItem(item);
  }

  private shouldQuoteTo(item: IItem, forceUpdate: boolean): boolean {
    if (!instanceOfIQuotable(item) || this.isQuoteRejectItem(item) || item.isDeleted) { return false; }

    // 最新フォーマットへの更新処理では各オブジェクトの引用する・しないの指定は判定しない
    if (forceUpdate) { return true; }

    // 制約の注入により編集不可となっているオブジェクトには引用させない
    if (instanceOfIRuleInjectable(item) && item.injectedRule && item.injectedRule.isEditDisabled) { return false; }

    return (item.shouldQuote && item.supportedQuotable);
  }

  /**
   * V1は固定文字については引用で値を上書きしない処理になっている
   * 転記では値が上書きされる動作になっているが、おそらく実装漏れと推測されている
   * ※引用元・引用先どちらかが固定文字なら除外するらしい
   */
  private isQuoteRejectItem(item: IItem): boolean {
    return instanceOfIQuotationRejecatable(item) && item.shouldRejectQuotation;
  }

  /**
   * 引用・転記にまつわる値のマージ処理
   * ・基本的には同じタイプのものだけ値を反映させる動作
   * ・チェック・円選択は互換を持たせる動作にはなっているらしい
   */
  private mergeObjectValue(sourceDocumentId: number, sourceFormatId: number, source: IItem, isByName: boolean, isSamePatient: boolean, handledPrivateIds: number[], forceUpdate: boolean, shouldQuoteTemplateId: boolean, ignoreSourceItemPrivateIds: number[], updatedPrivateIds: number[]): void {
    const updater = new OverwriteUpdater(source, isByName, shouldQuoteTemplateId);
    if (!updater.isKeyExist()) { return; }
    if (ignoreSourceItemPrivateIds.indexOf(source.privateId) >= 0) { return; }

    for (const item of this.source.itemSets[0].items) {
      if (!item || !this.shouldQuoteTo(item, forceUpdate)) { continue; }
      // すでに転記で値を格納したオブジェクトには値を格納しないようにする
      // 引用の場合はobjectIdがユニークのはずなので、挙動は変わらないはず
      if (handledPrivateIds.indexOf(item.privateId) >= 0) { continue; }
      if (!item.isAdditional && updater.isKeyMatched(item)) {
        if (!forceUpdate && this.shouldApplyValueToEmptyObjectOnly && !this.isEmptyItem(this.source, 0, item, updatedPrivateIds)) {
          // 空では無いので値を適用しない
          handledPrivateIds.push(item.privateId);
          break;
        }
        const cloned = item.clone();
        let isValueUpdated = false;
        const isImageSourceUpdated = this.clearImageSourceInformation(cloned);
    
        const info = this.createDocumentImageUpdateInformation(sourceDocumentId, sourceFormatId, source, isSamePatient);
        if (info) {
          info.itemSetId = 0;
          info.privateId = item.privateId;
          this._imageUpdateInformations.push(info);
        } else {
          isValueUpdated = updater.updateValues(cloned);
        }
        if (isValueUpdated || isImageSourceUpdated) {
          this.source.updateItem(cloned, {
            itemSetId: 0,
            isQuoting: true,
          });
          updatedPrivateIds.push(item.privateId);
        }
        handledPrivateIds.push(item.privateId);
        break;
      }
    }
  }

  /**
   * 円選択・チェックは単体ではなく連動する範囲での判定が必要
   * また、画像エリア・貼り付け画像については画像の有無の判定が必要
   */
  private isEmptyItem(editingDocument: EditingDocument, itemSetId: number, item: IItem, updatedPrivateIds: number[]): boolean {
    if (!editingDocument) { throw new Error("editingDocument is empty!!!"); }
    if (!item) { throw new Error("item is empty!!!"); }

    if (item instanceof AbstractImageDrawableItem) {
      // 画像エリア・貼り付け画像
      return !item.hasImage;
    }
    if (item instanceof AbstractCheckableItem) {
      // 円選択・チェック
      if (item.isChecked) {
        return false;
      }
      // 連動する判定ですべて入力されていなければ空と判定
      const relatedCheckedItems = this.extractRelatedCheckableItems(editingDocument, itemSetId, item).filter(i => i.isChecked);
      if (relatedCheckedItems.length === 0) { return true; }
      // すべて引用・転記で値が反映されたオブジェクトであれば空と判定（引用・転記で値が入る前は空だったと判定する）
      return Enumerable.from(relatedCheckedItems).all(i => updatedPrivateIds.indexOf(i.privateId) >= 0);
    }
    if (instanceOfIEmptyValidatable(item)) {
      return item.isEmpty;
    }
    // 基本的にここに到達するオブジェクトで引用・転記の対象となるものは無いはず
    return true;
  }

  private extractRelatedCheckableItems(editingDocument: EditingDocument, itemSetId: number, item: AbstractCheckableItem): AbstractCheckableItem[] {
    if (!item.groupKey) { return [item]; }
    
    const scopedItems = item.shouldLinkAsWideScope
      ? editingDocument.itemSets[itemSetId].items
      : this.extractSameSectionItems(editingDocument, itemSetId, item);

    return scopedItems
      .filter(i => !!i && i.typeKey === item.typeKey && i instanceof AbstractCheckableItem)
      .map(i => i as AbstractCheckableItem)
      .filter(i => i.groupKey === item.groupKey);
  }

  private extractSameSectionItems(editingDocument: EditingDocument, itemSetId: number, item: AbstractCheckableItem): IItem[] {
    const section = Enumerable.from(editingDocument.sections)
      .where(s => s.itemSetId === itemSetId)
      .firstOrDefault(s => s.itemPrivateIds.indexOf(item.privateId) >= 0);
    
    // 基本的には見つけられないことはないはず
    if (!section) { throw new Error("オブジェクトの配置されているページを特定することが出来ませんでした。"); }

    return section.itemPrivateIds.map(privateId => editingDocument.itemSets[itemSetId].items[privateId]);
  }

  private _imageUpdateInformations: DocumentImageUpdateInformation[] = [];

  public async copyImagesFromDocumentAsync(): Promise<void> {
    if (!this._imageUpdateInformations) { return; }
    for (const info of this._imageUpdateInformations) {
      console.debug(this._imageUpdateInformations);
      await this.copyImageFromDocumentAsync(info);
    }
    this._imageUpdateInformations = [];
  }

  private async copyImageFromDocumentAsync(info: DocumentImageUpdateInformation): Promise<void> {
    const repo = DomainRegistry.document.getDocumentRepository();
    const imageKey = (info.isCache || info.quotedFormatId)
      ? info.sourceImageKey
      : await repo.copyImageFromSavedDocument(info.sourceDocumentId, info.sourceImageKey);

    // originalImageKeyは空の場合もあるので注意すること
    const originalImageKey = await this.copyOriginalImageAsync(info, imageKey);
    
    const item = this.source.itemSets[info.itemSetId].items[info.privateId];
    const cloned = item.clone();
    if (!instanceOfIImageDrawable(cloned)) { throw new Error("invalid image item!!!"); }
    cloned.imageSource.imageGuid = imageKey;
    cloned.imageSource.imageSourceKind = info.quotedFormatId ? ImageSourceKinds.Format : ImageSourceKinds.Cache;
    cloned.imageSource.quotedFormatId = info.quotedFormatId;
    if (originalImageKey) {
      cloned.originalImageSource.imageGuid = originalImageKey;
      cloned.originalImageSource.imageSourceKind = ImageSourceKinds.Cache;
    } else {
      cloned.originalImageSource.imageGuid = null;
      cloned.originalImageSource.imageSourceKind = ImageSourceKinds.None;
    }
    cloned.imageKind = info.imageKind;
    cloned.claioImageId = info.claioImageId;
    cloned.imageDepartmentId = info.imageDepartmentId;
    cloned.rotationAngle = info.rotationAngle;
    cloned.externalImageId = info.externalImageId;

    // CNoteShcemaのレイヤー情報はとりあえず非対応としている
    cloned.drawLayerImageSource.imageGuid = null;
    cloned.drawLayerImageSource.imageSourceKind = ImageSourceKinds.None;
    cloned.drawObjectData = null;
    
    this.source.updateItem(cloned, {
      itemSetId: info.itemSetId,
      isQuoting: true,
    });
  }

  private async copyOriginalImageAsync(info: DocumentImageUpdateInformation, displayImageKey: string): Promise<string> {
    if (!info.originalSourceImageKey) {
      // もともと原本画像が無い
      return null;
    }
    if (info.sourceImageKey === info.originalSourceImageKey && info.isCache === info.isOriginalSourceCache) {
      // 表示画像と原本画像が同じ
      return displayImageKey;
    }
    if (info.isOriginalSourceCache) {
      // 現状でキャッシュ画像なので再取得の必要なし
      return info.originalSourceImageKey;
    }
    // TODO: 現状では登録済み文書にフォーマット由来の画像が入ることはないが、もし入るようになるのであれば修正が必要
    const repo = DomainRegistry.document.getDocumentRepository();
    return repo.copyImageFromSavedDocument(info.sourceDocumentId, info.originalSourceImageKey);
  }

  private copyAdditionalItems(valueSource: Document, isByName: boolean, forceUpdate: boolean, isSamePatient: boolean): void {
    // V1では対象を細かく指定できるが、現状では一律の判定で問題無い見込みのため、個別のオブジェクトの判定はしていない（必要になったら検討）
    if (!this.shouldCopyAdditionalItems(isByName, forceUpdate)) { return; }

    this._logger.info("追加オブジェクトをコピーします。");
    for (const section of valueSource.sections) {
      if (section.itemSetId !== 0) { continue; }
      for (const privateId of section.itemPrivateIds) {
        const item = valueSource.itemSets[0].items[privateId];
        if (!item || !item.isAdditional) { continue; }
        const updateSection = this.source.sections.find(s => s.formatSectionNumber === section.formatSectionNumber);
        if (!updateSection) { continue; }
        this.copyAdditionalItem(valueSource.documentId, valueSource.itemSets[0].formatId, item, updateSection, isSamePatient);
      }
    }
  }

  private shouldCopyAdditionalItems(isByName: boolean, forceUpdate: boolean): boolean {
    if (forceUpdate) { return true; }
    const configuration = store.getters.configuration as IConfiguration;
    return isByName
      ? configuration.behavior.document.editor.quotation.shouldCopyAllAdditionalItems.byObjectName
      : configuration.behavior.document.editor.quotation.shouldCopyAllAdditionalItems.byObjectId;
  }
  
  private copyAdditionalItem(sourceDocumentId: number, sourceFormatId: number, item: IItem, updateSection: Section, isSamePatient: boolean): void {
    const newPrivateId = this.source.itemSets[0].items.length;
    updateSection.itemPrivateIds.splice(updateSection.itemPrivateIds.length, 0, newPrivateId);
    const cloned = item.clone();
    cloned.privateId = newPrivateId;
    cloned.uniqueKey = null;        // 追加オブジェクトは値が入っていないはずだが、念のためクリアしておく
    cloned.sectionObjectUid = null; // 追加オブジェクトは値が入っていないはずだが、念のためクリアしておく
    this.clearImageSourceInformation(cloned);

    const info = this.createDocumentImageUpdateInformation(sourceDocumentId, sourceFormatId, item, isSamePatient);
    if (info) {
      info.itemSetId = 0;
      info.privateId = newPrivateId;
      this._imageUpdateInformations.push(info);
    }
    this.source.itemSets[0].items.splice(newPrivateId, 0, cloned);
  }

  private clearImageSourceInformation(item: IItem): boolean {
    if (!instanceOfIImageDrawable(item)) { return false; }
    if (item.imageSource && item.imageSource.imageSourceKind === ImageSourceKinds.Format) { return false; }
    item.clearImageInformation();
    return true;
  }

  /**
   * 更新先のitemSetIdとprivateIdは別途設定が必要。どこから情報を持ってくるかの情報だけ格納している。
   * 更新する必要がない場合はnullを返却します。
   */
  private createDocumentImageUpdateInformation(documentId: number, formatId: number, item: IItem, isSamePatient: boolean): DocumentImageUpdateInformation {
    if (!instanceOfIImageDrawable(item) || !item.imageSource.imageGuid || (item instanceof SignItem)) { return null; }  // imageGuidが無効の場合は画像が設定されてないので更新対象としない.
    // ImageSourceKinds.Formatは処理対象外だったが連鎖文書作成では作成中の文書からマージするので必要.更新対象とする.
    if ((isSamePatient || this.isOtherPatientDocumentQuotable) || item.imageKind === ImageKinds.Image || item.imageKind === null || item.imageKind === undefined) {
      const info = new DocumentImageUpdateInformation();
      info.itemSetId = null;
      info.privateId = null;
      info.sourceDocumentId = documentId;
      info.sourceImageKey = item.imageSource.imageGuid;
      info.imageKind = item.imageKind;
      info.claioImageId = item.claioImageId;
      info.imageDepartmentId = item.imageDepartmentId;
      info.rotationAngle = item.rotationAngle;
      info.externalImageId = item.externalImageId;
      info.isCache = (item.imageSource.imageSourceKind === ImageSourceKinds.Cache);
      // ImageSourceKinds.Formatの場合は引用元文書が作成中の場合のみ.quotedFormatId.
      info.quotedFormatId = (item.imageSource.imageSourceKind === ImageSourceKinds.Format)
        ? formatId
        : undefined;
      if (item.originalImageSource.imageSourceKind === ImageSourceKinds.Cache || item.originalImageSource.imageSourceKind === ImageSourceKinds.Document) {
        info.originalSourceImageKey = item.originalImageSource.imageGuid;
        info.isOriginalSourceCache = (item.originalImageSource.imageSourceKind === ImageSourceKinds.Cache);
      } else {
        // TODO: 現状では登録済み文書にフォーマット由来の画像が入ることは無いはずだが、もし入るようになるのであれば修正が必要
        info.originalSourceImageKey = null;
        info.isOriginalSourceCache = false;
      }
      if (!isSamePatient) {
        // 異なる患者の画像は他システムと連携するための情報をクリアして単なる画像として扱うようにする
        if (info.imageKind === ImageKinds.ClaioImage || info.imageKind === ImageKinds.ClaioMovie) {
          info.imageKind = ImageKinds.Image;
        }
        info.claioImageId = null;
        info.imageDepartmentId = null;
        info.externalImageId = null;
      }
      return info;
    }
    console.warn("異なる患者間での" + this.claioImageName + "の引用は対応していません。");
    return null;
  }

  private copyExtraSections(source: Document, isSamePatient: boolean, isByName: boolean): void {
    // itemSetIdが0のitemSetは続紙ではないのでコピーしない
    for (let itemSetId = 1; itemSetId < source.itemSets.length; ++itemSetId) {
      const itemSet = source.itemSets[itemSetId];

      // 続紙の中の画像をいったんキャッシュにコピーさせる対応
      const imageUpdateInformations: DocumentImageUpdateInformation[] = [];
      for (const item of itemSet.items) {
        if (!item) { continue; }
        const info = this.createDocumentImageUpdateInformation(source.documentId, itemSet.formatId, item, isSamePatient);
        if (info) {
          // insertItemSetでprivateIdが変わるようになるときは修正が必要
          info.privateId = item.privateId;
          imageUpdateInformations.push(info);
        }
        const cloned = item.clone();
        const isCleared = this.clearImageSourceInformation(cloned);
        if (isCleared) {
          // insertItemSet内でObject.freeze()するのでここでは不要
          itemSet.items.splice(cloned.privateId, 1, cloned);
        }
      }

      const sections = source.sections.filter(s => s.itemSetId === itemSetId);
      const targetSectionIndex = this.getTargetSectionIndexForCopiedExtraSection(source, isByName, sections);
      const newItemSetId = this.source.insertItemSet(itemSet, sections, targetSectionIndex);
      for (const info of imageUpdateInformations) {
        info.itemSetId = newItemSetId;
      }
      Array.prototype.push.apply(this._imageUpdateInformations, imageUpdateInformations);
    }
    this.applyHisInformationToExtraSections();
  }

  private getTargetSectionIndexForCopiedExtraSection(source: Document, isByName: boolean, sections: Section[]): number {
    if (isByName)  {
      // 末尾に追加する
      return this.source.sections.length;
    } else {
      // ページ位置を保持したままコピーする
      const sourceSectionIndex = (sections.length !== 0) ? source.sections.indexOf(sections[0]) : this.source.sections.length;
      return (0 <= sourceSectionIndex && sourceSectionIndex < this.source.sections.length)
        ? sourceSectionIndex
        : this.source.sections.length;
    }
  }

  /**
   * 続紙内のHIS項目を強制的に更新する
   * 京大で設定している複数タイミング指定には対応できないので、複数タイミング指定するサイトでは続紙に対象のHIS項目を置かないでもらう想定
   * ・患者IDなどの基本情報
   * ・作成者(CREATE_USER)などのWF由来の情報
   */
  private applyHisInformationToExtraSections(): void {
    const applicator = new HisInformationApplicator(this.source);
    applicator.applyHisInformationToExtraSections(this.source.hisInformation, store.getters.user, this.source.attributes.department, store.getters.orderUser, store.getters.attendingUser);
  }

  private removeExtraSections(): void {
    // forでまわしている内部でspliceすると意図したループができないので、concat()で対策している
    const sections = this.source.sections.concat();
    for (const section of sections) {
      if (section.itemSetId === 0) { continue; }
      this.source.removeSection(section);
    }
  }

  public async executeStackedScriptsAsyncWithResult(): Promise<boolean> {
    return this.source.executeStackedScriptsAsyncWithResult();
  }

  public async executeScriptInDocumentAtCalculateAsync(shouldAddToRequestStack: boolean): Promise<void> {
    return this.source.executeScriptInDocumentAsync(EditPhaseKinds.Calculate, shouldAddToRequestStack);
  }
}
